<script setup lang="ts">
import EmptyState from '@/components/EmptyState.vue'
import FinancingRequestPortfolioTable from '@/components/financing-requests/FinancingRequestPortfolioTable.vue'
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import PropertyStepErrors from '@/components/portfolio/PropertyStepErrors.vue'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData

type FinancingOrigin = 'new' | 'portfolio'

const props = defineProps<{
  origin: FinancingOrigin | null
  portfolioObjects: EstateObjectData[]
  selectedId: string | null
  errors: string[]
}>()

const emit = defineEmits<{
  'update:origin': [origin: FinancingOrigin]
  select: [estateObject: EstateObjectData]
}>()

const { t } = useI18n()

const choices = computed<{ origin: FinancingOrigin; icon: string; meta: string }[]>(() => [
  { origin: 'new', icon: 'pi pi-building-columns', meta: t('choices.new.meta') },
  {
    origin: 'portfolio',
    icon: 'pi pi-briefcase',
    meta: t('choices.portfolio.meta', { count: props.portfolioObjects.length }, props.portfolioObjects.length),
  },
])

const createPropertyHref = EstateObjectsController.create({ query: { for_financing_request: 1 } })

const selectById = (id: string): void => {
  const estateObject = props.portfolioObjects.find((candidate) => candidate.id === id)

  if (estateObject) {
    emit('select', estateObject)
  }
}
</script>

<template>
  <FinestCard :title="t('title')" icon="pi pi-question-circle">
    <p class="text-ink-2 mb-4 text-sm">{{ t('hint') }}</p>

    <PropertyStepErrors :messages="errors" class="mb-4" />

    <div class="grid gap-3 sm:grid-cols-2">
      <button
        v-for="choice in choices"
        :key="choice.origin"
        type="button"
        class="focus-visible:ring-action/35 flex cursor-pointer gap-3.5 rounded-[10px] border p-5 text-left transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
        :class="
          origin === choice.origin
            ? 'border-action bg-info-subtle'
            : 'border-line hover:border-action hover:bg-info-subtle'
        "
        :aria-pressed="origin === choice.origin"
        @click="emit('update:origin', choice.origin)">
        <span
          class="flex size-11 flex-none items-center justify-center rounded-xl border transition-colors"
          :class="
            origin === choice.origin ? 'border-action bg-action text-white' : 'border-line bg-surface text-action'
          ">
          <i :class="choice.icon" class="text-xl!" aria-hidden="true" />
        </span>

        <span class="min-w-0">
          <span class="text-ink block font-bold">{{ t(`choices.${choice.origin}.title`) }}</span>
          <span class="text-ink-2 mt-1 block text-sm">{{ t(`choices.${choice.origin}.description`) }}</span>
          <span class="text-ink-3 mt-2 block text-xs">{{ choice.meta }}</span>
        </span>
      </button>
    </div>

    <div v-if="origin === 'portfolio'" class="border-line-2 mt-5 border-t pt-5">
      <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 class="section-label section-label--sm mb-1">{{ t('portfolio.title') }}</h3>
          <p class="text-ink-2 text-sm">{{ t('portfolio.hint') }}</p>
        </div>

        <Link v-if="portfolioObjects.length" :href="createPropertyHref">
          <FinestButton variant="secondary" size="sm" icon="pi pi-plus" :label="t('portfolio.create')" />
        </Link>
      </div>

      <EmptyState
        v-if="!portfolioObjects.length"
        icon="pi pi-building"
        :title="t('portfolio.empty.title')"
        :description="t('portfolio.empty.description')">
        <template #action>
          <Link :href="createPropertyHref">
            <FinestButton icon="pi pi-plus" :label="t('portfolio.empty.action')" />
          </Link>
        </template>
      </EmptyState>

      <FinancingRequestPortfolioTable
        v-else
        :estate-objects="portfolioObjects"
        :selected-id="selectedId"
        @select="selectById" />
    </div>
  </FinestCard>
</template>

<i18n>
{
  "de": {
    "title": "Was soll finanziert werden?",
    "hint": "Diese Antwort entscheidet über den weiteren Verlauf: Eine Portfolio-Immobilie ist bereits erfasst – bei einem Ankauf legen wir das Objekt gemeinsam an.",
    "choices": {
      "new": {
        "title": "Neue Immobilie finanzieren",
        "description": "Ein Objekt, das noch nicht in deinem Portfolio liegt – Ankauf, Zuschlag oder Bauvorhaben.",
        "meta": "Wir legen es in den nächsten Schritten an."
      },
      "portfolio": {
        "title": "Immobilie aus dem Bestand refinanzieren",
        "description": "Ein Objekt aus deinem Portfolio – Anschlussfinanzierung, Modernisierung oder Kapitalbeschaffung.",
        "meta": "Keine Immobilie verfügbar. | {count} Immobilie verfügbar. | {count} Immobilien verfügbar."
      }
    },
    "portfolio": {
      "title": "Welches Objekt aus deinem Portfolio?",
      "hint": "Eine Anfrage bezieht sich auf genau ein Objekt. Im nächsten Schritt trägst du die gewünschte Finanzierung ein.",
      "create": "Immobilie anlegen",
      "empty": {
        "title": "Keine Immobilie verfügbar",
        "description": "Lege zuerst eine Immobilie an oder finanziere eine neue Immobilie.",
        "action": "Immobilie anlegen"
      }
    }
  },
  "en": {
    "title": "What are you financing?",
    "hint": "This answer decides what follows: a portfolio property is already captured – for a purchase we capture the property together.",
    "choices": {
      "new": {
        "title": "Finance a new property",
        "description": "A property that is not in your portfolio yet – purchase, awarded bid or construction project.",
        "meta": "We capture it in the next steps."
      },
      "portfolio": {
        "title": "Finance a portfolio property",
        "description": "A property from your portfolio – follow-up financing, modernisation or raising capital.",
        "meta": "No property available. | {count} property available. | {count} properties available."
      }
    },
    "portfolio": {
      "title": "Which property from your portfolio?",
      "hint": "A request covers exactly one property. You enter the financing you want in the next step.",
      "create": "Create property",
      "empty": {
        "title": "No property available",
        "description": "Create a property first, or finance a new property instead.",
        "action": "Create property"
      }
    }
  }
}
</i18n>
